import { Box } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useEffect, useMemo, useState } from 'react';
import { KnownHubDetailsDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useHasDoubleSidedTower } from '../../../lockers/list/hooks/use-has-double-sided-tower.hook';
import {
  HubCardWithTowers,
  HubCardWithTowersModeType
} from '../../../../ui/components/pure/virtual-hub/hub-card-with-towers/hub-card-with-towers.component';
import { mapTowerModelToRenderData } from '../../../../ui/components/pure/virtual-hub/tower-renderer/tower-render-data.mapper';

export interface HubPreviewTowerRow {
  model: string;
  inventoryID: string;
}

export interface HubLayoutPreviewProps {
  towers: HubPreviewTowerRow[];
  hubName?: string;
  frontName?: string;
  backName?: string;
  initialTowers?: HubPreviewTowerRow[];
  isEditMode?: boolean;
  hubType?: KnownHubDetailsDtoType;
}

export function HubLayoutPreview({
  towers,
  hubName,
  frontName,
  backName,
  initialTowers,
  isEditMode,
  hubType
}: HubLayoutPreviewProps) {
  const resolvedFrontName = frontName || 'outside';
  const resolvedBackName = backName || 'inside';

  const [selectedSide, setSelectedSide] = useState(resolvedFrontName);

  const towerModelNames = useMemo(() => towers.map((t) => t.model).filter(Boolean), [towers]);
  const { hasDoubleSided, towerModels } = useHasDoubleSidedTower(towerModelNames);

  const sideSelectOptions = useMemo(
    () => (hasDoubleSided ? [resolvedFrontName, resolvedBackName] : []),
    [hasDoubleSided, resolvedFrontName, resolvedBackName]
  );

  useEffect(() => {
    setSelectedSide(resolvedFrontName);
  }, [hasDoubleSided, resolvedFrontName]);

  const hasReordered = useMemo(() => {
    if (!isEditMode || !initialTowers || initialTowers.length === 0) return false;
    if (towers.length !== initialTowers.length) return false;
    return towers.some(
      (t, i) => t.model !== initialTowers[i]?.model || t.inventoryID !== initialTowers[i]?.inventoryID
    );
  }, [towers, initialTowers, isEditMode]);

  const validTowers = towers.filter((t) => t.model);

  const towerRenderData = useMemo(() => {
    let lockerOffset = 0;
    return validTowers
      .map((tower, index) => {
        const model = towerModels.find((m) => m.name === tower.model);
        if (!model) return null;
        const data = mapTowerModelToRenderData(model, index, tower.inventoryID, lockerOffset, hubType);
        lockerOffset += (model.uiLockerLayouts ?? []).length;
        return data;
      })
      .filter((t): t is NonNullable<typeof t> => t !== null);
  }, [validTowers, towerModels, hubType]);

  const maxModuleHeight = useMemo(() => {
    if (towerRenderData.length === 0) return 0;
    return Math.max(...towerRenderData.map((t) => t.pixelDimensions2D.heightNumber));
  }, [towerRenderData]);

  if (towerRenderData.length === 0) {
    return null;
  }

  return (
    <Box>
      <HubCardWithTowers
        headerProps={{
          hubName: hubName ?? 'Hub 1',
          hubStatus: '',
          statusColor: 'transparent',
          statusIcon: () => <></>,
          deliveryContainerType: 'locker',
          deliveryContainerCount: towerRenderData.reduce(
            (sum, t) => sum + t.lockers.filter((l) => !l.isKiosk).length,
            0
          ),
          deliveryContainerCountsBySize: [],
          actions: [],
          isPreview: true,
          ...(hasReordered && {
            alertText:
              'If you reorder towers you will also need to change the physical settings on their dispswitches.',
            alertIcon: <WarningIcon fontSize="small" />
          })
        }}
        towers={towerRenderData}
        maxModuleHeight={maxModuleHeight}
        isDoubleSided={hasDoubleSided}
        sideSelectOptions={sideSelectOptions}
        onSelectSide={setSelectedSide}
        selectedSide={hasDoubleSided ? selectedSide : undefined}
        mode={{ type: HubCardWithTowersModeType.Preview }}
      />
    </Box>
  );
}
